import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { supabase } from './supabase';

/**
 * Take a photo with the camera
 */
export async function takePhoto(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) {
    console.warn('Camera is only available on native platforms');
    return null;
  }

  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Camera,
      width: 512,
      height: 512,
    });

    return image.dataUrl || null;
  } catch (error) {
    console.error('Error taking photo:', error);
    return null;
  }
}

/**
 * Pick an image from gallery
 */
export async function pickFromGallery(): Promise<string | null> {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos,
      width: 512,
      height: 512,
    });

    return image.dataUrl || null;
  } catch (error) {
    console.error('Error picking from gallery:', error);
    return null;
  }
}

/**
 * Convert data URL to Blob
 */
async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return response.blob();
}

/**
 * Upload avatar to Supabase Storage
 */
export async function uploadAvatarToStorage(
  dataUrl: string,
  userId: string
): Promise<{ url: string | null; error: string | null }> {
  try {
    // Convert data URL to blob
    const blob = await dataUrlToBlob(dataUrl);

    const fileName = `${userId}/${Date.now()}.jpg`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('avatars')
      .upload(fileName, blob, {
        contentType: 'image/jpeg',
        upsert: true,
      });

    if (error) {
      console.error('Upload error:', error);
      return { url: null, error: error.message };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('avatars')
      .getPublicUrl(data.path);

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    console.error('Error uploading avatar:', error);
    return {
      url: null,
      error: error instanceof Error ? error.message : 'Failed to upload avatar'
    };
  }
}

/**
 * Delete avatar from storage
 */
export async function deleteAvatarFromStorage(avatarUrl: string): Promise<boolean> {
  try {
    // Extract path from URL
    const urlParts = avatarUrl.split('/avatars/');
    if (urlParts.length < 2) return false;

    const path = urlParts[1];

    const { error } = await supabase.storage
      .from('avatars')
      .remove([path]);

    if (error) {
      console.error('Delete error:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting avatar:', error);
    return false;
  }
}
